import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { haptic } from '@/lib/telegram';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  noHaptic?: boolean;
}

const variants: Record<Variant, string> = {
  primary: 'bg-accent text-white hover:bg-accent-strong shadow-glow',
  secondary: 'bg-bg-elevated text-white/90 hover:bg-bg-muted border border-white/5',
  ghost: 'text-white/70 hover:text-white hover:bg-white/5',
  danger: 'bg-danger/90 text-white hover:bg-danger',
};

const sizes: Record<Size, string> = {
  sm: 'text-sm px-3 py-2 rounded-lg min-h-[36px]',
  md: 'text-base px-4 py-3 rounded-xl min-h-[44px]',
  lg: 'text-lg px-5 py-4 rounded-2xl min-h-[52px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', fullWidth, className, noHaptic, onClick, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-medium transition select-none touch-manipulation',
        'active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100',
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        className,
      )}
      onClick={(e) => {
        if (!noHaptic) haptic('light');
        onClick?.(e);
      }}
      {...rest}
    >
      {children}
    </button>
  );
});
